import i18n from '../i18n';
import { toast } from '../app/toast';
import { getAccessToken, login, renewSilently } from '../auth/userManager';
import { config } from '../config';
import { getSupportSessionId } from './support';
import type { ProblemDetails } from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly title: string;
  readonly detail?: string;
  readonly errors: { field: string; message: string }[];
  /** Уже показано пользователю (уведомление о версии, режиме поддержки, входе). */
  handled = false;

  constructor(p: ProblemDetails) {
    super(p.detail || p.title);
    this.name = 'ApiError';
    this.status = p.status;
    this.type = p.type;
    this.title = p.title;
    this.detail = p.detail;
    this.errors = p.errors ?? [];
  }

  get isStaleVersion(): boolean {
    return this.status === 409 && this.type.endsWith('stale-version');
  }

  /** Ошибка конкретного поля: точное совпадение или вложенное (patient.last_name). */
  fieldError(field: string): string | undefined {
    return this.errors.find((e) => e.field === field || e.field.endsWith('.' + field))?.message;
  }

  /** Понятный текст для уведомления. */
  get userMessage(): string {
    if (this.errors.length) return this.errors.map((e) => e.message).join('. ');
    return this.detail || this.title;
  }
}

/** Вызывается при «Обновить» после конфликта версий; задаётся в main.tsx (refetch всех запросов). */
let onRefreshRequested: () => void = () => window.location.reload();
export const setRefreshHandler = (fn: () => void): void => {
  onRefreshRequested = fn;
};

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return `${config.apiBase}${path}${s ? '?' + s : ''}`;
}

async function parseError(res: Response): Promise<ApiError> {
  let p: Partial<ProblemDetails> = {};
  try {
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('json')) p = (await res.json()) as Partial<ProblemDetails>;
  } catch {
    /* тело не JSON */
  }
  return new ApiError({
    type: p.type ?? 'about:blank',
    title: p.title ?? i18n.t('errors.http', { status: res.status }),
    status: p.status ?? res.status,
    detail: typeof p.detail === 'string' ? p.detail : undefined,
    errors: Array.isArray(p.errors) ? p.errors : [],
  });
}

async function send(url: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = await getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const support = getSupportSessionId();
  if (support) headers['X-Support-Session'] = support;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  return fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  let res: Response;
  try {
    res = await send(url, opts);
    if (res.status === 401) {
      // Один раз пробуем обновить токен, затем — на страницу входа.
      const user = await renewSilently();
      if (user) res = await send(url, opts);
      if (res.status === 401) {
        void login();
        const e = await parseError(res);
        e.handled = true;
        throw e;
      }
    }
  } catch (e) {
    if (e instanceof ApiError || (e instanceof DOMException && e.name === 'AbortError')) throw e;
    throw new ApiError({ type: 'network', title: i18n.t('errors.network'), status: 0 });
  }

  if (!res.ok) {
    const err = await parseError(res);
    if (err.isStaleVersion) {
      err.handled = true;
      toast(i18n.t('errors.staleVersion'), {
        tone: 'error',
        action: { label: i18n.t('common.refresh'), onClick: () => onRefreshRequested() },
      });
    } else if (err.status === 403 && getSupportSessionId()) {
      err.handled = true;
      toast(err.detail || i18n.t('support.readOnlyToast'), { tone: 'error' });
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('json')) return undefined as T;
  return (await res.json()) as T;
}

/** Показ ошибки уведомлением, если её ещё не показали. */
export function notifyError(err: unknown): void {
  if (err instanceof ApiError) {
    if (err.handled) return;
    err.handled = true;
    toast(err.userMessage, { tone: 'error' });
    return;
  }
  if (err instanceof DOMException && err.name === 'AbortError') return;
  toast(i18n.t('errors.unknown'), { tone: 'error' });
}
